import React from 'react';
import styled from 'styled-components/macro';

import modularScale from 'styles/helpers/modular-scale';
import formatDate from 'helpers/formatDate';

import colors from 'styles/themes/colors';
import { numbersTypography } from 'styles/global/typography/typography';

const TrackerBackground = styled('rect')<any>`
  fill: ${colors.Grey};
  fill-opacity: ${({ isGhostTracker }): number =>
    isGhostTracker ? 0.25 : 0.15};
`;

const TrackerLine = styled('line')<any>`
  stroke: ${({ theme }): string => theme.CasperGrey};
  stroke-width: 1;
`;

const Pointer = styled('circle')<any>`
  stroke: white;
  stroke-width: 1.5;
  fill: ${({ fillColor }): string => fillColor};
`;

const Text = styled('text')`
  font-size: ${modularScale(-2)};
  font-weight: 600;
  fill: ${({ theme }): string => theme.DeepBlue};
  ${numbersTypography};
`;

const TrackerText: React.FC<any> = (props) => {
  const { date, isNoStreams, height, margin, width, xCoord } = props;
  /** Width of the shadow rectangular under the current date */
  const widthShadowRect = 120;

  // Pre-calculate some offsets and settings for the tracker
  let trackerTextAlign = 'middle';
  // Left 10% of the graph
  if (xCoord < width * 0.1) {
    trackerTextAlign = 'start';
  }
  // Right 10% of the graph
  if (xCoord > width * 0.9) {
    trackerTextAlign = 'end';
  }
  const currentDate = formatDate(date, 'day');
  const yText = isNoStreams ? height - margin.topHorizontalAxe - 7 : height - 2;
  const yFilter = isNoStreams
    ? height - margin.topHorizontalAxe - margin.betweenGraphs - 3
    : height - margin.topHorizontalAxe;

  return (
    <>
      <defs>
        <linearGradient id="gradient">
          <stop offset="5%" stopColor="white" stopOpacity="0" />
          <stop offset="20%" stopColor="whitesmoke" />
          <stop offset="80%" stopColor="whitesmoke" />
          <stop offset="95%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect
        fill="url(#gradient)"
        x={xCoord - widthShadowRect / 2}
        y={yFilter}
        width={widthShadowRect}
        height="15"
      />
      <Text x={xCoord} y={yText} textAnchor={trackerTextAlign}>
        &nbsp;{currentDate}&nbsp;
      </Text>
    </>
  );
};

const Tracker: React.FC<any> = (props) => {
  const {
    color,
    date,
    hasNoData,
    height,
    isGhostTracker,
    margin,
    width,
    widthLeftLabel,
    xCoord = 0,
    yData = [],
  } = props;
  const bandWidth = 7;
  const checkNumberFinite = (value: any): boolean =>
    isFinite(value) && value !== null;
  /** Decrease by 20 for top and 20 for bottom sides or by space between graphs */
  const y1 = hasNoData.position ? 35 : 20;
  const y2 = hasNoData.stream
    ? height - 20 - margin.betweenGraphs
    : height - 20;
  const heightBackground =
    hasNoData.stream || hasNoData.position
      ? height - 2 * 20 - margin.betweenGraphs
      : height - 2 * 20;

  return (
    <g transform={`translate(${margin.left + widthLeftLabel}, 0)`}>
      <TrackerBackground
        width={bandWidth}
        height={heightBackground}
        x={xCoord - bandWidth / 2}
        y={y1}
        isGhostTracker={isGhostTracker}
      />

      <TrackerLine
        x1={xCoord}
        x2={xCoord}
        y1={y1}
        y2={y2}
        isGhostTracker={isGhostTracker}
      />

      {yData.map((item: any) => {
        return (
          checkNumberFinite(item) && (
            <Pointer
              key={item}
              cx={xCoord}
              cy={item}
              r="5"
              fillColor={color}
              isGhostTracker={isGhostTracker}
            />
          )
        );
      })}
      <TrackerText
        date={date}
        height={height}
        isNoStreams={hasNoData.stream}
        margin={margin}
        width={width}
        xCoord={xCoord}
      />
    </g>
  );
};

export default Tracker;
